import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiCalendar, FiClock, FiUser, FiArrowLeft, FiCheck, FiLayers, FiShield, FiTarget, FiZap, FiArrowRight } from 'react-icons/fi';
import CTAButton from '../components/CTAButton';
import SEO from '../components/SEO';
import { blogPosts } from '../data/blogPosts';
import { BUSINESS } from '../config/business';
import { MEDIA } from '../config/media';

function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const blogImages = Object.values(MEDIA.blogThumbs);
  const postIndex = blogPosts.findIndex(p => p.slug === slug);
  const postImage = blogImages[postIndex % blogImages.length];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "image": postImage?.src,
    "author": {
      "@type": "Organization",
      "name": "BPAS Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": BUSINESS.name,
      "logo": {
        "@type": "ImageObject",
        "url": "https://i.ibb.co/kgj60dnL/logo.webp"
      }
    },
    "datePublished": post.date,
    "dateModified": post.date,
    "description": post.excerpt
  };

  const relatedPosts = blogPosts
    .filter(p => p.slug !== post.slug)
    .slice(0, 3);

  const renderBlock = (block, index) => {
    switch (block.type) {
      case 'p': return <p key={index} className="text-base sm:text-lg text-slate-700 leading-relaxed mb-8" dangerouslySetInnerHTML={{ __html: block.text }} />;
      case 'h2': return <h2 key={index} id={block.text.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-')} className="text-2xl sm:text-3xl font-bold text-slate-900 mt-12 mb-8 pb-3 border-b border-slate-200 leading-tight tracking-tight">{block.text}</h2>;
      case 'ul': return ( 
        <ul key={index} className="space-y-4 mb-10 ml-2 sm:ml-4"> 
          {block.items.map((item, i) => ( 
            <li key={i} className="flex items-start text-slate-700 text-base sm:text-lg leading-relaxed"> 
              <SafeIcon icon={FiCheck} className="h-5 w-5 text-blue-600 mr-4 mt-1 shrink-0" /> 
              <span>{item}</span> 
            </li> 
          ))} 
        </ul> 
      );
      case 'cta': return ( 
        <div key={index} className="bg-slate-900 text-white rounded-3xl p-8 sm:p-16 text-center my-16 shadow-2xl relative overflow-hidden"> 
          <h3 className="text-2xl sm:text-4xl font-bold mb-6 tracking-tight">{block.heading}</h3> 
          <p className="text-slate-300 text-base sm:text-xl mb-12 max-w-2xl mx-auto">{block.text}</p> 
          <CTAButton size="lg" className="w-full sm:w-auto px-12">{block.buttonText}</CTAButton> 
        </div> 
      );
      default: return null;
    }
  };

  return ( 
    <main className="bg-white overflow-x-hidden text-left"> 
      <SEO title={post.title} description={post.excerpt} schema={[articleSchema]} /> 
      
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-12 sm:py-20 border-b border-slate-200"> 
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8 font-bold group"> 
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" /> Back to Articles 
          </Link> 
          <div className="flex flex-wrap items-center text-xs sm:text-sm text-slate-500 mb-8 gap-x-6 gap-y-4 font-medium uppercase tracking-wider"> 
            <div className="flex items-center"><SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" /> <time dateTime={post.date}>Published {post.date}</time></div> 
            <div className="flex items-center"><SafeIcon icon={FiClock} className="h-4 w-4 mr-2" /> <span>{post.readingTime}</span></div> 
            <div className="flex items-center"><SafeIcon icon={FiUser} className="h-4 w-4 mr-2" /> <span>{post.author}</span></div> 
          </div> 
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-8 leading-tight tracking-tight">{post.title}</h1> 
        </div> 
      </section> 

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"> 
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12"> 
          <div className="lg:col-span-8"> 
            <article className="max-w-3xl mx-auto lg:mx-0"> 
              <div className="w-full h-[250px] sm:h-[500px] bg-slate-100 rounded-3xl overflow-hidden shadow-xl mb-12"> 
                <img src={postImage?.src} alt={post.title} className="w-full h-full object-cover" loading="eager" width="800" height="500" /> 
              </div> 
              <div className="rich-text-content"> 
                {post.blocks.map((block, index) => renderBlock(block, index))} 
              </div> 
            </article> 

            <section className="mt-20 pt-16 border-t border-slate-200"> 
              <h2 className="text-2xl font-bold text-slate-900 mb-10">Related Articles</h2> 
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8"> 
                {relatedPosts.slice(0, 2).map((rp, i) => ( 
                  <Link key={i} to={`/blog/${rp.slug}`} className="group block"> 
                    <h3 className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors mb-2 leading-tight">{rp.title}</h3> 
                    <p className="text-sm text-slate-500 line-clamp-2">{rp.excerpt}</p> 
                  </Link> 
                ))} 
              </div> 
            </section> 
          </div> 

          <aside className="lg:col-span-4 space-y-12"> 
            <div className="bg-blue-600 text-white p-8 rounded-3xl shadow-xl sticky top-28"> 
              <h3 className="text-xl font-bold mb-4">Start Your Automation Journey</h3> 
              <p className="text-blue-100 mb-8 leading-relaxed">Let's discuss how we can implement these specific strategies for your Australian business. ABN-registered, Sydney-based expertise.</p> 
              <CTAButton className="w-full bg-white text-blue-600 hover:bg-blue-50">Book Free Call</CTAButton> 
            </div> 
          </aside> 
        </div> 
      </div>

      <section className="py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-10">More from BPA Solutions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/services" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiLayers} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Our Services</h3>
              <p className="text-sm text-slate-500 mb-4">Automation and design services for SMBs.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">Browse All <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/about" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiShield} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">About Us</h3>
              <p className="text-sm text-slate-500 mb-4">Learn about our Sydney-based consultancy.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">About Team <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/resources" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiZap} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Free Resources</h3>
              <p className="text-sm text-slate-500 mb-4">Downloadable tools for your business.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">View Tools <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/contact" className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiTarget} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Get Started</h3>
              <p className="text-sm text-slate-500 mb-4">Book your free 20-minute strategy call.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">Contact Us <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
          </div>
        </div>
      </section>
    </main> 
  );
}

export default BlogPost;